import { Form } from 'antd';
import { useGetAllCultureQuery, useGetTransportQuery } from 'app/api';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useTaskSlice } from 'app/containers/Task/slice';
import { selectRoutesWithoutTaskFilters } from 'app/containers/Task/slice/selectors';
import {
  IChangedFormField,
  IRoutesWithoutTaskFilters,
} from 'app/containers/Task/slice/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IOption } from 'types';
import moment, { Moment } from 'moment';
import { formatRouteDateFilter } from 'utils/helpers';

const FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR = '___';

export const useRoutesWithoutTaskFiltersCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const taskActions = useTaskSlice().actions;

  const envId = useSelector(selectSelectedEnvId) as number;
  const filters = useSelector(selectRoutesWithoutTaskFilters);

  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<IRoutesWithoutTaskFilters>(
    {},
  );

  const [form] = Form.useForm();

  const {
    currentData: transportResponse,
    isFetching: isTransportLoading,
  } = useGetTransportQuery(
    {
      envId,
      pageSize: 100,
      currentPage: 0,

      companyGroupId: null,
      companyId: null,
      filters: {},
      sort: {
        order: 'ascend',
        field: 'model',
      },
    },
    { skip: !envId },
  );

  const { data: cultureResponse } = useGetAllCultureQuery();

  const cultureOptions: IOption[] =
    cultureResponse?.map(culture => ({
      value: culture.id,
      label: culture.name,
    })) || [];

  const transportOptions: IOption<number>[] =
    transportResponse?.content
      .filter(transport => transport.gpsVehicle?.id)
      .map(transport => ({
        value: transport.gpsVehicle?.id as number,
        label: transport.model || transport.gpsVehicle?.id + '',
      })) || [];

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen, filters]);

  // Set filter values from state on open
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const {
      startDate,
      endDate,
      cultureIds,
      gpsVehicleIds,
      durationFrom,
      durationTo,
    } = localFilters;

    form.setFieldsValue({
      date: [
        startDate ? moment(startDate) : undefined,
        endDate ? moment(endDate) : undefined,
      ],
      cultureIds,
      gpsVehicleIds,
      durationFrom,
      durationTo,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setFilters = (payload: IRoutesWithoutTaskFilters) => {
    setLocalFilters(payload);
    dispatch(taskActions.setDebouncedRoutesWithoutTaskFilters(payload));
  };

  const onClose = () => setIsOpen(false);

  const onOpen = (event: React.MouseEvent<Element, MouseEvent>) => {
    event.stopPropagation();
    setIsOpen(true);
  };

  const onFormChange = (values: IChangedFormField<Moment[] | number[]>[]) => {
    if (!values.length) {
      return;
    }
    const changeObj = values[0];
    const name = (changeObj.name as string[])[0];
    const value = changeObj.value;

    if (name === 'date') {
      let startDate = undefined;
      let endDate = undefined;

      if (Array.isArray(value)) {
        startDate = formatRouteDateFilter({
          momentValue: value[0] as Moment | undefined,
          isStartOfDay: true,
        });
        endDate = formatRouteDateFilter({
          momentValue: value[1] as Moment | undefined,
          isStartOfDay: false,
        });
      }
      const dateFilter = {
        startDate,
        endDate,
      };
      setFilters({ ...localFilters, ...dateFilter });
      return;
    }

    setFilters({ ...localFilters, [name]: value });
  };

  const getAllFilterOptions = () => {
    const {
      startDate,
      endDate,
      gpsVehicleIds,
      cultureIds,
      durationFrom,
      durationTo,
    } = localFilters;
    const options: IOption<string | number>[] = [];

    if (durationFrom) {
      options.push({
        label: `${durationFrom}min`,
        value: `durationFrom${FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR}${durationFrom}`,
      });
    }

    if (durationTo) {
      options.push({
        label: `${durationTo}min`,
        value: `durationTo${FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR}${durationTo}`,
      });
    }

    if (startDate) {
      const startDateText = moment(startDate).format('YYYY-MM-DD');
      options.push({
        label: startDateText,
        value: `startDate${FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR}${startDate}`,
      });
    }

    if (endDate) {
      const endDateText = moment(endDate).format('YYYY-MM-DD');
      options.push({
        label: endDateText,
        value: `endDate${FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR}${endDate}`,
      });
    }

    if (gpsVehicleIds?.length) {
      gpsVehicleIds.forEach(id => {
        const transportOption = transportOptions.find(
          transport => transport.value === id,
        );
        if (transportOption) {
          options.push({
            label: transportOption.label,
            value: `gpsVehicleIds${FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR}${transportOption.value}`,
          });
        }
      });
    }

    if (cultureIds?.length) {
      cultureIds.forEach(id => {
        const cultureOption = cultureOptions.find(
          culture => culture.value === id,
        );
        if (cultureOption) {
          options.push({
            label: cultureOption.label,
            value: `cultureIds${FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR}${cultureOption.value}`,
          });
        }
      });
    }

    const values = options.map(option => option.value);

    return { values, options };
  };

  const { options, values } = getAllFilterOptions();

  const onFilterDeselect = (value: React.ReactText) => {
    const [propertyName, propertyValue]: string[] = String(value).split(
      FILTER_TAG_PROPERTY_NAME_AND_VALUE_SEPARATOR,
    );

    if (
      propertyName === 'startDate' ||
      propertyName === 'endDate' ||
      propertyName === 'durationFrom' ||
      propertyName === 'durationTo'
    ) {
      setFilters({ ...localFilters, [propertyName]: undefined });
    } else {
      const filterProperty: number[] | undefined = (localFilters as any)[
        propertyName
      ];

      if (Array.isArray(filterProperty)) {
        setFilters({
          ...localFilters,
          [propertyName]: filterProperty.filter(
            id => id !== Number(propertyValue),
          ),
        });
      }
    }
  };
  return {
    onClose,
    t,
    form,
    onFormChange,
    onOpen,
    isOpen,
    options,
    values,
    transportOptions,
    isTransportLoading,
    onFilterDeselect,
  };
};
